// The ONE rule for hiding a path-borne secret from every analytics surface.
//
// `/enterprise/pay/<token>` (#349) carries the customer's pay-link token IN THE
// PATH, and the token is that page's whole authorization. Every place that
// hands a pathname to an analytics sink - `AnalyticsContext` (GA4 page_view,
// the first-party beacon, `app_route_viewed`) and `services/analytics.js`
// (`normalizeRoutePath` for the product-event queue, `sendToBackend` for
// `utm_visit` and friends) - runs it through `maskTokenPath` first. One module,
// so there is exactly one list of token-bearing routes to keep right.
//
// It is an explicit, route-NAMED rule. It does not depend on what the token
// looks like (length, digits, charset), which is what the id-shape heuristic in
// `normalizeRoutePath` does and why that heuristic alone is not enough.
//
// Pure and dependency-free, so both callers and a unit test can import it.

/** The route every Enterprise pay-link page is reported as. */
export const ENTERPRISE_PAY_TOKEN_ROUTE = '/enterprise/pay/:token';

const ENTERPRISE_PAY_PREFIX = '/enterprise/pay/';
// The one route under that prefix that carries no token: HYP's return page.
const ENTERPRISE_PAY_RESULT = '/enterprise/pay/result';

/**
 * A pathname with any path-borne SECRET replaced by its route pattern.
 *
 * Every path under `/enterprise/pay/` other than `/enterprise/pay/result`
 * (trailing slash tolerated) becomes `/enterprise/pay/:token`. Matched
 * case-INSENSITIVELY and after collapsing repeated slashes, because the router
 * matches `/Enterprise/Pay/<token>` and `//enterprise/pay/<token>` too and a
 * case-sensitive mask would pass those through in the clear. A query string or
 * fragment on a token-bearing path is dropped with it. Everything else is
 * returned unchanged; a non-string is returned as given.
 *
 * @param {string} path
 * @returns {string}
 */
export const maskTokenPath = (path) => {
  if (typeof path !== 'string' || path.length === 0) return path;
  const folded = path.split('?')[0].split('#')[0].replace(/\/{2,}/g, '/').toLowerCase();
  if (!folded.startsWith(ENTERPRISE_PAY_PREFIX)) return path;
  const trimmed = folded.length > 1 && folded.endsWith('/') ? folded.slice(0, -1) : folded;
  if (trimmed === ENTERPRISE_PAY_RESULT) return path;
  return ENTERPRISE_PAY_TOKEN_ROUTE;
};

/**
 * True when `path` carries a secret `maskTokenPath` would hide.
 *
 * @param {string} path
 * @returns {boolean}
 */
export const isTokenBearingPath = (path) => maskTokenPath(path) !== path;
